import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { FileText, Loader2, FileDown, Wallet, CalendarDays, Building2, LogOut } from "lucide-react";
import { toast } from "sonner";

const eur = (v) => (v == null ? "—" : Number(v).toLocaleString("fr-FR", { style: "currency", currency: "EUR" }));

export default function MyPayslipsPage() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/payroll/my/payslips").then((r) => setData(r.data)).catch(() => setData({ employees: [], payslips: [] })).finally(() => setLoading(false));
  }, [api]);

  const openBlob = async (url, errMsg) => {
    try {
      const r = await api.get(url, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
    } catch (e) {
      toast.error(errMsg);
    }
  };
  const openPdf = (p) => openBlob(`/payroll/payslips/${p.payslip_id}/pdf`, "Bulletin indisponible");
  const exitDocs = data?.exit_documents || [];

  const slips = data?.payslips || [];
  const last = slips[0];
  const emp = data?.employees?.[0];

  return (
    <div className="p-6 md:p-8 max-w-4xl" data-testid="my-payslips-page">
      <PageHeader icon={<FileText />} title="Mes bulletins de paie" description="Vos bulletins sont mis à disposition ici dès leur publication — conservez-les sans limitation de durée." testId="my-payslips-header" />
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div> : slips.length === 0 ? (
        <div className="bg-[#14101c] border border-white/10 rounded-xl p-8 text-center text-slate-500 text-sm" data-testid="my-payslips-empty">
          {emp ? "Aucun bulletin publié pour le moment." : "Aucun dossier salarié n'est rattaché à votre compte."}
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-3 gap-3 mb-6">
            <div className="bg-[#14101c] border border-white/5 rounded-xl p-4"><div className="flex items-center gap-2 text-[11px] text-slate-500"><Wallet className="w-3.5 h-3.5 text-[#22c55e]" /> Dernier net payé · {last.label}</div><div className="text-xl font-bold text-[#22c55e] mt-1" data-testid="my-last-net">{eur(last.net_to_pay)}</div></div>
            <div className="bg-[#14101c] border border-white/5 rounded-xl p-4"><div className="flex items-center gap-2 text-[11px] text-slate-500"><CalendarDays className="w-3.5 h-3.5 text-[#f7c935]" /> Solde congés payés</div><div className="text-xl font-bold text-white mt-1">{last.leave?.balance_after ?? emp?.cp_balance ?? "—"} j</div></div>
            <div className="bg-[#14101c] border border-white/5 rounded-xl p-4"><div className="flex items-center gap-2 text-[11px] text-slate-500"><Building2 className="w-3.5 h-3.5 text-[#38bdf8]" /> Employeur</div><div className="text-sm font-bold text-white mt-1 truncate">{last.societe_name}</div><div className="text-[10px] text-slate-500">{emp?.job_title} · {emp?.matricule}</div></div>
          </div>
          {exitDocs.length > 0 && (
            <div className="bg-[#14101c] border border-amber-500/30 rounded-xl p-4 mb-4" data-testid="my-exit-documents">
              <div className="flex items-center gap-2 text-xs font-bold text-amber-300 uppercase tracking-wider"><LogOut className="w-3.5 h-3.5" /> Documents de fin de contrat</div>
              <div className="text-[11px] text-slate-400 mt-1">Fin de contrat le {exitDocs[0].end_date} — {exitDocs[0].societe_name}. Conservez ces documents : ils vous seront demandés par France Travail et vos organismes.</div>
              <div className="flex flex-wrap gap-2 mt-3">
                {exitDocs.map((d) => (
                  <button key={d.kind} onClick={() => openBlob(d.url, "Document indisponible")} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs text-white" data-testid={`my-exit-doc-${d.kind}`}><FileDown className="w-3.5 h-3.5" /> {d.label}</button>
                ))}
              </div>
            </div>
          )}
          <div className="bg-[#14101c] border border-white/10 rounded-xl divide-y divide-white/5">
            {slips.map((p) => (
              <div key={p.payslip_id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`my-payslip-${p.payslip_id}`}>
                <div className="w-9 h-9 rounded-lg bg-[#f7c935]/10 flex items-center justify-center"><FileText className="w-4 h-4 text-[#f7c935]" /></div>
                <div className="flex-1 min-w-[160px]">
                  <div className="text-sm font-bold text-white capitalize">{p.label} {p.is_final && <span className="ml-1 text-[9px] font-black px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 align-middle" data-testid={`my-payslip-final-${p.payslip_id}`}>SOLDE DE TOUT COMPTE</span>}</div>
                  <div className="text-[11px] text-slate-500">{p.societe_name} · publié le {(p.published_at || "").slice(0, 10)}{p.paid_at ? ` · payé le ${p.paid_at}` : ""}</div>
                </div>
                <div className="text-right"><div className="text-[10px] text-slate-500">Brut</div><div className="text-xs font-mono text-slate-300">{eur(p.gross)}</div></div>
                <div className="text-right"><div className="text-[10px] text-slate-500">Net payé</div><div className="text-sm font-mono font-bold text-[#22c55e]">{eur(p.net_to_pay)}</div></div>
                <button onClick={() => openPdf(p)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs text-white" data-testid={`my-payslip-pdf-${p.payslip_id}`}><FileDown className="w-3.5 h-3.5" /> PDF</button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
